import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Input } from 'semantic-ui-react';
import _ from 'lodash';
import { injectIntl } from 'react-intl';
import * as constants from '../inputConstants';
import ValidationErrorMessage from '../../ValidationErrorMessage/ValidationErrorMessage';
import './InputWrapper.styles.less';

class InputWrapper extends React.PureComponent {
  static propTypes = {
    allowTyping: PropTypes.bool,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    fluid: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.oneOf([
      constants.INTEGER_TYPE,
      constants.STRING_TYPE,
      constants.CURRENCY_TYPE,
      constants.DATE_TYPE,
      constants.EMAIL,
      constants.PASSWORD_TYPE
    ]),
    name: PropTypes.string,
    placeholder: PropTypes.string,
    icon: PropTypes.string,
    iconPosition: PropTypes.string,
    step: PropTypes.number,
    onChange: PropTypes.func,
    onKeyDown: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    onPaste: PropTypes.func,
    prefix: PropTypes.string,
    error: PropTypes.string,
    errorMessage: PropTypes.string,
    autoComplete: PropTypes.bool,
    min: PropTypes.number,
    max: PropTypes.number,
    label: PropTypes.string,
    labelPosition: PropTypes.string,
    loading: PropTypes.bool,
    resetValueOnOverflow: PropTypes.bool,
    showNumericUpAndDown: PropTypes.bool,
    validate: PropTypes.func
  };

  static defaultProps = {
    allowTyping: true,
    type: constants.STRING_TYPE,
    autoComplete: true,
    resetValueOnOverflow: true
  };

  constructor(props) {
    super(props);
    this.handleChange = this.handleChange.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onInputRef = this.onInputRef.bind(this);
    this.state = { value: props.value };
  }

  componentWillReceiveProps(nextProps) {
    this.setState({ value: nextProps.value });
  }

  onBlur(e) {
    if (this.props.onBlur) {
      this.props.onBlur(e);
    }
    if (this.props.onChange && this.props.allowTyping) {
      this.props.onChange(e, e.target);
    }
  }

  onKeyDown = (e) => {
    if (this.props.onKeyDown && this.props.allowTyping) {
      this.props.onKeyDown(e, e.target);
    }
  };

  onInputRef(el) {
    if (!this.props.autoComplete && el && el.inputRef) {
      el.inputRef.autocomplete = 'off';
    }
  }

  handleChange(e, data) {
    if (!this.props.allowTyping) {
      return;
    }
    const maxInput = 2147483647;
    if (this.props.type === constants.INTEGER_TYPE && data.value >= maxInput) {
      // todo: replace console.error with toaster
      console.error(`${data.name} reached max integer size ${maxInput}`);
      if (this.props.resetValueOnOverflow) {
        data.value = 0;
      } else {
        return;
      }
    }

    this.validate(data.value);
    this.setState({ value: data.value });
  }

  changeValue(changeFunc) {
    const value = Number(this.state.value);
    const currentValue = Number.isNaN(value) ? 0 : value;
    const newValue = changeFunc(currentValue);
    if (this.isValidInteger(newValue)) {
      this.setState({ value: newValue });
    }
  }

  isValidInteger = (newValue) => {
    const { min, max } = this.props;

    if (min && max) {
      return newValue >= min && newValue <= max;
    }

    if (min) {
      return newValue >= min;
    }

    if (max) {
      return newValue <= max;
    }

    return true;
  }

  validate(value) {
    if (!this.props.validate || typeof this.props.validate !== 'function') {
      return;
    }

    if (this.props.type === constants.EMAIL) {
      this.props.validate({
        value,
        isValid: this.isValidEmail(value),
        errorKey: 'bad.email.error.message'
      });
    }

    if (this.props.type === constants.INTEGER_TYPE) {
      const valueNum = Number(value);
      this.props.validate({
        value,
        isValid: !Number.isNaN(valueNum) && this.isValidInteger(valueNum),
        errorKey: 'bad.number.value'
      });
    }

    if (this.props.type === constants.ALPHANUMERIC) {
      this.props.validate({
        value,
        isValid: this.isAlphanumeric(value),
        errorKey: 'bad.alphanumeric.value'
      });
    }
  }

  isValidEmail(email) {
    const mail = _.trim(email).toLowerCase();
    const pattern = /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;
    return mail !== '' && pattern.test(mail);
  }

  isAlphanumeric(value) {
    const alphanumericString = _.trim(value);
    const pattern = /^[a-zA-Z0-9.]*/;
    return alphanumericString !== '' && pattern.test(alphanumericString);
  }

  unhidePassword = () => {
    const typeOverride = this.state.typeOverride;
    if (!typeOverride || typeOverride === constants.PASSWORD_TYPE) {
      this.setState({ typeOverride: 'text' });
    } else {
      this.setState({ typeOverride: constants.PASSWORD_TYPE });
    }
  }

  render() {
    const { formatMessage } = this.props.intl; // eslint-disable-line react/prop-types
    const {
      className,
      type,
      disabled,
      name,
      placeholder,
      icon,
      iconPosition,
      step,
      fluid,
      prefix,
      error,
      errorMessage,
      onKeyDown,
      onFocus,
      onPaste,
      min,
      max,
      label,
      labelPosition,
      loading
    } = this.props;

    const clsName = classNames(className, { inputValueError: !!error });

    const typeCalculated = this.state.typeOverride
      ? this.state.typeOverride
      : type;

    const numericUpAndDown =
      this.props.type === constants.INTEGER_TYPE &&
      this.props.showNumericUpAndDown ? (
        <div className="quantity-nav">
          <div
            role="button"
            className="quantity-button quantity-up"
            onClick={() => this.changeValue((value) => value + 1)}
          >
            <i aria-hidden="true" className="icon como-ic-triangle-up" />
          </div>
          <div
            role="button"
            className="quantity-button quantity-down"
            onClick={() =>
              this.changeValue((value) => (value > 0 ? value - 1 : 0))
            }
          >
            <i aria-hidden="true" className="icon como-ic-triangle-down" />
          </div>
        </div>
      ) : null;

    return (
      <div className="input-wrapper">
        <Input
          className={clsName}
          type={typeCalculated}
          icon={icon}
          iconPosition={iconPosition}
          step={step}
          fluid={fluid}
          onKeyDown={this.onKeyDown}
          onChange={this.handleChange}
          onBlur={this.onBlur}
          ref={this.onInputRef}
          onFocus={onFocus}
          onPaste={onPaste}
          disabled={disabled}
          data-automation-id={prefix}
          name={name}
          placeholder={placeholder}
          min={min}
          label={label}
          labelPosition={labelPosition}
          loading={loading}
          value={this.state.value}
        />

        {numericUpAndDown}

        {type === constants.PASSWORD_TYPE ? (
          <i
            role="button"
            onClick={this.unhidePassword}
            className={`icon eye ${this.state.typeOverride === 'text' ? '' : 'slash'}`}
          />
        ) : null}

        {error || errorMessage ? (
          <ValidationErrorMessage
            errorMessage={errorMessage || formatMessage({ id: error })}
            dataAutomationId={`${prefix}.error.message`}
          />
        ) : null}
      </div>
    );
  }
}

export default injectIntl(InputWrapper);
